import { useQuery } from "@tanstack/react-query";
import { LinkIcon, Maximize2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { getSharedPublished } from "../api/shareLinks";
import { Button, Card, EmptyState } from "../ds";
import { PagePicker } from "../shell/PagePicker";
import type { TvFramePageInfo } from "../shell/TvFrame";
import { FitModeToggle, TvFrameViewer, type TvFrameViewerHandle, type ViewerFitMode } from "../shell/TvFrameViewer";
import { PREVIEW_STAGE_BG, PreviewThemeToggle, useViewerTheme } from "../shell/TvFrameTheme";

/** /s/:token -- a share link: the published report, no sign-in, no app shell.
 *
 * Renders the published version exactly like the studio's published preview
 * (same renderer, the page's own canvas uniformly scaled, no per-browser theme).
 * `?theme=light|dark` picks the colour mode; the toggle only changes this view. */
export function ShareViewerPage() {
  const { token = "" } = useParams();
  const shared = useQuery({ queryKey: ["shared", token], queryFn: () => getSharedPublished(token), retry: false, staleTime: 60_000 });
  const [pages, setPages] = useState<TvFramePageInfo[]>([]);
  const [pageId, setPageId] = useState<string | null>(null);
  const definition = shared.data?.definition as { name?: string; settings?: { defaultTheme?: unknown } } | undefined;
  const [theme, setTheme] = useViewerTheme(definition?.settings?.defaultTheme);
  const [fitMode, setFitMode] = useState<ViewerFitMode>("page");
  const viewer = useRef<TvFrameViewerHandle>(null);
  const name = definition?.name;

  useEffect(() => {
    document.title = name ? `${name} · Maridive Reports` : "Maridive Reports";
  }, [name]);

  if (shared.isError) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 16, background: "var(--bg)" }}>
        <Card>
          <EmptyState icon={LinkIcon} title="This link isn't available" description={shared.error instanceof Error ? shared.error.message : "It may have expired or been turned off. Ask the person who sent it for a new link."} />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: PREVIEW_STAGE_BG[theme], display: "flex", flexDirection: "column" }}>
      <div className="ds-row" style={{ padding: "10px 16px", gap: 12, flexWrap: "wrap", background: "var(--surface)", borderBottom: "1px solid var(--divider)" }}>
        <strong className="ds-grow ds-truncate" style={{ minWidth: 0, fontSize: 16 }}>{name ?? "…"}</strong>
        {pages.length > 1 ? <PagePicker pages={pages} value={pageId} onChange={(id) => { setPageId(id); viewer.current?.frame?.post({ type: "mrp:selectPage", pageId: id }); }} /> : null}
        <FitModeToggle value={fitMode} onChange={setFitMode} />
        <PreviewThemeToggle compact theme={theme} onChange={setTheme} />
        <Button icon={Maximize2} onClick={() => viewer.current?.toggleFullscreen()} aria-label="Full screen" title="Full screen">Full screen</Button>
      </div>
      <div style={{ flex: 1, padding: 12 }}>
        {shared.data ? (
          <TvFrameViewer
            ref={viewer}
            reportId={shared.data.reportId}
            source="share"
            shareToken={token}
            theme={theme}
            onThemeChange={setTheme}
            fitMode={fitMode}
            onFitMode={setFitMode}
            pages={pages}
            pageId={pageId}
            reserve={90}
            onLoaded={(ps, id) => { setPages(ps); setPageId(id); }}
            onPage={setPageId}
          />
        ) : null}
      </div>
    </div>
  );
}
